import React from "react";
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type PlatformCandidateData = components["schemas"]["PlatformCandidateResponse"];
type EntityType = components["schemas"]["EntityType"];

interface CandidateSelectorProps {
  selectedPlatform: BriefingPlatform;
  /** Drives whether to read insertionOrders or campaigns from the API response */
  defaultCampaignLevel: "CAMPAIGN" | "INSERTIONORDER";
  sourceId: string;
  setSourceId: (id: string) => void;
  /**
   * Optional. When provided, the selector reports the level of the chosen entity
   * (CAMPAIGN vs INSERTIONORDER) so platforms like Spotify can pick either a
   * campaign or an ad set within the same list.
   */
  setCampaignLevel?: (level: EntityType) => void;
  /** Optional. Reports the advertiserId of the chosen entity. */
  setAdvertiserId?: (id: string) => void;
  nativeAdProvider?: "TABOOLA" | "OUTBRAIN" | null;
  setNativeAdProvider?: (provider: "TABOOLA" | "OUTBRAIN" | null) => void;
  canFetchCampaigns: boolean;
  isBusy: boolean;
  candidatesError: unknown;
  candidatesData: PlatformCandidateData | null | undefined;
  handleSave?: () => void;
  hideManualEntry?: boolean;
}

export const CandidateSelector: React.FC<CandidateSelectorProps> = ({
  selectedPlatform,
  defaultCampaignLevel,
  sourceId,
  setSourceId,
  setCampaignLevel,
  setAdvertiserId,
  nativeAdProvider,
  setNativeAdProvider,
  canFetchCampaigns,
  isBusy,
  candidatesError,
  candidatesData,
  handleSave,
  hideManualEntry = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sourceIdTab, setSourceIdTab] = React.useState<0 | 1>(0);
  const [filter, setFilter] = React.useState("");

  const campaigns = React.useMemo(
    () => candidatesData?.campaigns ?? [],
    [candidatesData]
  );
  const adSets = React.useMemo(
    () => candidatesData?.insertionOrders ?? [],
    [candidatesData]
  );

  /**
   * Grouped "one or the other" mode: show campaigns with their ad sets nested
   * underneath, letting the user pick either. Enabled generically whenever the
   * platform defaults to CAMPAIGN level but the response also carries ad sets
   * (e.g. Spotify). DV360 keeps its flat insertion-order list (level === INSERTIONORDER).
   */
  const isGroupedMode = defaultCampaignLevel === "CAMPAIGN" && adSets.length > 0;

  const allCandidates = React.useMemo(() => {
    if (!candidatesData) return [];

    // Use the server-provided level to decide which entity list to show
    if (defaultCampaignLevel === "INSERTIONORDER") {
      return (candidatesData.insertionOrders ?? []).map((io) => ({
        id: io.id,
        name: io.name,
        originalPlatform: io.platform,
      }));
    }

    // Otherwise, show campaigns
    return (candidatesData.campaigns ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      originalPlatform: c.originalPlatform,
    }));
  }, [candidatesData, defaultCampaignLevel]);

  const filteredCampaigns = React.useMemo(() => {
    let campaignsToFilter = allCandidates;
    if (selectedPlatform === "NATADS" && nativeAdProvider) {
      campaignsToFilter = allCandidates.filter(
        (c) => c.originalPlatform === nativeAdProvider
      );
    }

    let result = campaignsToFilter;
    if (filter.trim()) {
      const q = filter.toLowerCase();
      result = campaignsToFilter.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          (c.id ?? "").toLowerCase().includes(q)
      );
    }

    // Always sort the selected campaign to the top
    return result.sort((a, b) => {
      if (a.id === sourceId) return -1;
      if (b.id === sourceId) return 1;
      return 0;
    });
  }, [allCandidates, filter, nativeAdProvider, selectedPlatform, sourceId]);

  // Grouped candidates: each campaign with its nested ad sets, filtered by search.
  const groupedCandidates = React.useMemo(() => {
    if (!isGroupedMode)
      return {
        groups: [] as Array<{
          campaign: components["schemas"]["PlatformCandidateCampaign"];
          adSets: components["schemas"]["PlatformCandidateInsertionOrder"][];
          visibleAdSets: components["schemas"]["PlatformCandidateInsertionOrder"][];
          campaignMatches: boolean;
        }>,
        orphanAdSets:
          [] as components["schemas"]["PlatformCandidateInsertionOrder"][],
      };
    const q = filter.trim().toLowerCase();
    const matches = (name?: string, id?: string) =>
      !q ||
      (name ?? "").toLowerCase().includes(q) ||
      (id ?? "").toLowerCase().includes(q);

    const groups = campaigns.map((c) => {
      const childAdSets = adSets.filter((a) => a.campaignId === c.id);
      const campaignMatches = matches(c.name, c.id);
      const visibleAdSets = campaignMatches
        ? childAdSets
        : childAdSets.filter((a) => matches(a.name, a.id));
      return { campaign: c, adSets: childAdSets, visibleAdSets, campaignMatches };
    });

    const knownCampaignIds = new Set(campaigns.map((c) => c.id));
    const orphanAdSets = adSets.filter(
      (a) => !knownCampaignIds.has(a.campaignId) && matches(a.name, a.id)
    );

    return { groups, orphanAdSets };
  }, [isGroupedMode, campaigns, adSets, filter]);

  /** Select a campaign: level CAMPAIGN, sourceId = campaign id. */
  const selectCampaign = (
    candidate: components["schemas"]["PlatformCandidateCampaign"]
  ) => {
    if (candidate.id === sourceId) {
      setSourceId("");
      setCampaignLevel?.(defaultCampaignLevel);
      setAdvertiserId?.("");
      return;
    }
    setSourceId(candidate.id ?? "");
    setCampaignLevel?.("CAMPAIGN");
    setAdvertiserId?.(candidate.advertiserId ?? "");
  };

  /** Select an ad set: level INSERTIONORDER, sourceId = ad set id. */
  const selectAdSet = (
    adSet: components["schemas"]["PlatformCandidateInsertionOrder"]
  ) => {
    if (adSet.id === sourceId) {
      setSourceId("");
      setCampaignLevel?.(defaultCampaignLevel);
      setAdvertiserId?.("");
      return;
    }
    setSourceId(adSet.id ?? "");
    setCampaignLevel?.("INSERTIONORDER");
    setAdvertiserId?.(adSet.advertiserId ?? "");
  };

  const renderSelectableRow = (
    id: string | undefined,
    name: string | undefined,
    onClick: () => void,
    opts?: { nested?: boolean; chipLabel?: string }
  ) => {
    const isSelected = id === sourceId && !!id;
    return (
      <Box
        onClick={onClick}
        sx={{
          pl: opts?.nested ? 4 : 2,
          pr: 2,
          py: 1.2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: isSelected
            ? theme.palette.primary.light + "22"
            : "transparent",
          "&:hover": { bgcolor: theme.palette.action.hover },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
              {name}
            </Typography>
            {opts?.chipLabel && (
              <Chip
                size="small"
                label={opts.chipLabel}
                variant="outlined"
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            ID: {id}
          </Typography>
        </Box>
        {isSelected && (
          <CheckIcon
            fontSize="small"
            color="primary"
            sx={{ ml: 1, flexShrink: 0 }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        {t("briefingsView.sourceId", "Source ID")}
      </Typography>

      {canFetchCampaigns ? (
        <>
          <Tabs
            value={sourceIdTab}
            onChange={(_, v) => setSourceIdTab(v as 0 | 1)}
            variant="fullWidth"
            sx={{
              mb: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tab
              label={t("briefingsView.fromPlatform", "Browse {{platform}}", {
                platform: selectedPlatform,
              })}
              value={0}
            />
            {!hideManualEntry && (
              <Tab
                label={t("briefingsView.manualEntry", "Manual entry")}
                value={1}
              />
            )}
          </Tabs>

          {sourceIdTab === 0 ? (
            <Stack spacing={1}>
              <TextField
                size="small"
                placeholder={t("common.search", "Search by name or ID...")}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
              />
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                {isBusy ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : candidatesError ? (
                  <Alert severity="error" sx={{ m: 1 }}>
                    {t(
                      "briefingsView.campaignFetchError",
                      "Failed to load campaigns. Use manual entry."
                    )}
                  </Alert>
                ) : isGroupedMode ? (
                  groupedCandidates.groups.filter(
                    (g) => g.campaignMatches || g.visibleAdSets.length > 0
                  ).length === 0 &&
                  groupedCandidates.orphanAdSets.length === 0 ? (
                    <Box py={3} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "briefingsView.noCampaignsFound",
                          "No campaigns found"
                        )}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {groupedCandidates.groups
                        .filter(
                          (g) =>
                            g.campaignMatches || g.visibleAdSets.length > 0
                        )
                        .map((group, idx) => (
                          <React.Fragment key={group.campaign.id}>
                            {idx > 0 && <Divider />}
                            {renderSelectableRow(
                              group.campaign.id,
                              group.campaign.name,
                              () => selectCampaign(group.campaign),
                              { chipLabel: t("briefingsView.campaign", "Campaign") }
                            )}
                            {group.visibleAdSets.map((adSet) => (
                              <React.Fragment key={adSet.id}>
                                <Divider />
                                {renderSelectableRow(
                                  adSet.id,
                                  adSet.name,
                                  () => selectAdSet(adSet),
                                  {
                                    nested: true,
                                    chipLabel: t(
                                      "briefingsView.adSet",
                                      "Ad set"
                                    ),
                                  }
                                )}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      {groupedCandidates.orphanAdSets.length > 0 && (
                        <>
                          <Divider />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", px: 2, py: 1 }}
                          >
                            {t("briefingsView.otherAdSets", "Other ad sets")}
                          </Typography>
                          {groupedCandidates.orphanAdSets.map((adSet) => (
                            <React.Fragment key={adSet.id}>
                              <Divider />
                              {renderSelectableRow(
                                adSet.id,
                                adSet.name,
                                () => selectAdSet(adSet),
                                {
                                  nested: true,
                                  chipLabel: t("briefingsView.adSet", "Ad set"),
                                }
                              )}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  )
                ) : filteredCampaigns.length === 0 ? (
                  <Box py={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "briefingsView.noCampaignsFound",
                        "No campaigns found"
                      )}
                    </Typography>
                  </Box>
                ) : (
                  filteredCampaigns.map((campaign, idx) => {
                    const isSelected = campaign.id === sourceId;
                    return (
                      <React.Fragment key={campaign.id}>
                        {idx > 0 && <Divider />}
                        <Box
                          onClick={() => {
                            if (isSelected) {
                              setSourceId("");
                              if (
                                selectedPlatform === "NATADS" &&
                                setNativeAdProvider
                              ) {
                                setNativeAdProvider(null);
                              }
                            } else {
                              setSourceId(campaign.id ?? "");
                              if (
                                selectedPlatform === "NATADS" &&
                                campaign.originalPlatform &&
                                setNativeAdProvider
                              ) {
                                setNativeAdProvider(
                                  campaign.originalPlatform as
                                    | "TABOOLA"
                                    | "OUTBRAIN"
                                );
                              }
                            }
                          }}
                          sx={{
                            px: 2,
                            py: 1.2,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: isSelected
                              ? theme.palette.primary.light + "22"
                              : "transparent",
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 700 : 400}
                            >
                              {campaign.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {campaign.id}
                            </Typography>
                          </Box>
                          {isSelected && (
                            <CheckIcon
                              fontSize="small"
                              color="primary"
                              sx={{ ml: 1, flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })
                )}
              </Box>
              {sourceId && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t("common.selected", "Selected")}:
                  </Typography>
                  <Chip
                    size="small"
                    label={sourceId}
                    onDelete={() => {
                      setSourceId("");
                      setCampaignLevel?.(defaultCampaignLevel);
                      setAdvertiserId?.("");
                    }}
                  />
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField
                label={t("briefingsView.sourceId", "Source ID")}
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                fullWidth
                placeholder={t(
                  "briefingsView.sourceIdPlaceholder",
                  "Optional — can be added later"
                )}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && handleSave) {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            </Stack>
          )}
        </>
      ) : (
        <Stack spacing={2}>
          <TextField
            label={t("briefingsView.sourceId", "Source ID")}
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            fullWidth
            placeholder={t(
              "briefingsView.sourceIdPlaceholder",
              "Optional — can be added later"
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter" && handleSave) {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </Stack>
      )}
    </Box>
  );
};
